import React from 'react';
import {
  Box, Typography, TextField, MenuItem, FormControlLabel, Checkbox, Button, Divider, IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const PropertyPanel = ({ element, onChange, onDelete }) => {
  if (!element) {
    return (
      <Box sx={{ width: 260, p: 2, borderLeft: '1px solid #ddd' }}>
        <Typography variant="body2" color="text.secondary">
          Select an element to edit its properties
        </Typography>
      </Box>
    );
  }

  const update = (field, value) => {
    onChange({ ...element, [field]: value });
  };

  const handleTableTypeChange = (newType) => {
    const seatCount = element.seatCount || 2;
    let subType = element.subType;
    let shape = element.shape;
    let wheelchairAccessible = element.wheelchairAccessible;

    switch (newType) {
      case 'standard':
        subType = `standard-${seatCount}`;
        shape = seatCount <= 4 ? 'square' : 'rectangle';
        wheelchairAccessible = true;
        break;
      case 'booth':
        subType = `booth-${seatCount}`;
        shape = 'rounded-rect';
        wheelchairAccessible = false;
        break;
      case 'bar-stool':
        subType = 'bar-stool';
        shape = 'circle';
        wheelchairAccessible = false;
        break;
      case 'high-top':
        subType = `high-top-${seatCount}`;
        shape = 'square';
        wheelchairAccessible = false;
        break;
      case 'high-table':
        subType = `high-table-${seatCount}`;
        shape = 'square';
        wheelchairAccessible = false;
        break;
      case 'counter':
        subType = 'counter-seat';
        shape = 'square';
        wheelchairAccessible = true;
        break;
      default:
        break;
    }

    onChange({ ...element, tableType: newType, subType, shape, wheelchairAccessible });
  };

  const isTable = element.type === 'table';
  const isFixed = element.type === 'fixed';
  const isZone = element.type === 'zone';
  const isDoor = element.subType === 'door';
  const isFan = element.subType === 'fan';
  const isRestroom = element.subType === 'restroom' || element.subType === 'disabled_bathroom';
  const isParking = element.subType === 'disabled_parking';
  const isSliding = element.openMechanism === 'sliding';
  const isRevolving = element.openMechanism === 'revolving';
  const isPushPull = element.openMechanism === 'push' || element.openMechanism === 'pull';

  return (
    <Box role="region" aria-label="Properties panel" sx={{ width: 260, overflowY: 'auto', p: 2, borderLeft: '1px solid #ddd' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>Properties</Typography>
        <IconButton size="small" color="error" aria-label={`Delete ${element.label || element.subType}`} onClick={() => onDelete(element.id)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        {element.type} — {element.subType}
      </Typography>

      <TextField
        label="Label"
        size="small"
        fullWidth
        value={element.label || ''}
        onChange={(e) => update('label', e.target.value)}
        sx={{ mb: 1 }}
      />

      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField label="Width" size="small" type="number" value={element.width} onChange={(e) => update('width', Math.max(20, Number(e.target.value)))} sx={{ flex: 1 }} />
        <TextField label="Height" size="small" type="number" value={element.height} onChange={(e) => update('height', Math.max(20, Number(e.target.value)))} sx={{ flex: 1 }} />
      </Box>

      <TextField label="Rotation" size="small" type="number" fullWidth value={element.rotation || 0} onChange={(e) => update('rotation', Number(e.target.value))} sx={{ mb: 1 }} />

      <Divider sx={{ my: 1 }} />

      {/* Table-specific fields */}
      {isTable && (
        <Box role="group" aria-label="Table properties">
          <TextField label="Table No" size="small" type="number" fullWidth value={element.tableNo || ''} onChange={(e) => update('tableNo', Number(e.target.value))} sx={{ mb: 1 }} />
          <TextField label="Seat Count" size="small" type="number" fullWidth value={element.seatCount || 2} onChange={(e) => update('seatCount', Number(e.target.value))} sx={{ mb: 1 }} />
          <TextField label="Table Type" size="small" select fullWidth value={element.tableType || 'standard'} onChange={(e) => handleTableTypeChange(e.target.value)} sx={{ mb: 1 }}>
            <MenuItem value="standard">Standard</MenuItem>
            <MenuItem value="booth">Booth</MenuItem>
            <MenuItem value="bar-stool">Bar Stool</MenuItem>
            <MenuItem value="high-top">High-Top</MenuItem>
            <MenuItem value="high-table">High-Table</MenuItem>
            <MenuItem value="counter">Counter</MenuItem>
          </TextField>
          <TextField label="Zone" size="small" select fullWidth value={element.zone || 'inside'} onChange={(e) => update('zone', e.target.value)} sx={{ mb: 1 }}>
            <MenuItem value="inside">Inside</MenuItem>
            <MenuItem value="outside">Outside</MenuItem>
            <MenuItem value="covered_outdoor">Covered Outdoor</MenuItem>
          </TextField>
          <FormControlLabel control={<Checkbox size="small" checked={!!element.wheelchairAccessible} onChange={(e) => update('wheelchairAccessible', e.target.checked)} />} label="Wheelchair Accessible" />
          <FormControlLabel control={<Checkbox size="small" checked={!!element.elevated} onChange={(e) => update('elevated', e.target.checked)} />} label="Elevated" />
        </Box>
      )}

      {/* Door-specific fields */}
      {isDoor && (
        <Box role="group" aria-label="Door properties">
          <TextField label="Open Mechanism" size="small" select fullWidth value={element.openMechanism || 'push'} onChange={(e) => update('openMechanism', e.target.value)} sx={{ mb: 1 }}>
            <MenuItem value="push">Push</MenuItem>
            <MenuItem value="pull">Pull</MenuItem>
            <MenuItem value="sliding">Sliding</MenuItem>
            <MenuItem value="revolving">Revolving</MenuItem>
          </TextField>
          {isPushPull && (
            <TextField label="Open Direction" size="small" select fullWidth value={element.openDirection || 'inward'} onChange={(e) => update('openDirection', e.target.value)} sx={{ mb: 1 }}>
              <MenuItem value="inward">Inward</MenuItem>
              <MenuItem value="outward">Outward</MenuItem>
            </TextField>
          )}
          <FormControlLabel control={<Checkbox size="small" checked={!!element.automatic} onChange={(e) => update('automatic', e.target.checked)} />} label="Automatic" />
          <TextField label="Door Weight" size="small" select fullWidth value={element.doorWeight || 'standard'} onChange={(e) => update('doorWeight', e.target.value)} sx={{ mb: 1 }}>
            <MenuItem value="light">Light</MenuItem>
            <MenuItem value="standard">Standard</MenuItem>
            <MenuItem value="heavy">Heavy</MenuItem>
          </TextField>
          <TextField label="Door Width" size="small" select fullWidth value={element.doorWidth || 'standard'} onChange={(e) => update('doorWidth', e.target.value)} sx={{ mb: 1 }}>
            <MenuItem value="standard">Standard</MenuItem>
            <MenuItem value="wide">Wide</MenuItem>
            <MenuItem value="double">Double</MenuItem>
          </TextField>
          <TextField label="Door Type" size="small" select fullWidth value={element.doorType || 'main'} onChange={(e) => update('doorType', e.target.value)} sx={{ mb: 1 }}>
            <MenuItem value="main">Main Entrance</MenuItem>
            <MenuItem value="side">Side Entrance</MenuItem>
            <MenuItem value="emergency">Emergency Exit</MenuItem>
            <MenuItem value="internal">Internal</MenuItem>
          </TextField>
          {isSliding && (
            <FormControlLabel control={<Checkbox size="small" checked={!!element.trackFlush} onChange={(e) => update('trackFlush', e.target.checked)} />} label="Track Flush with Ground" />
          )}
          {isRevolving && (
            <>
              <FormControlLabel control={<Checkbox size="small" checked={!!element.revolvingPause} onChange={(e) => update('revolvingPause', e.target.checked)} />} label="Pause Feature" />
              <FormControlLabel control={<Checkbox size="small" checked={!!element.revolvingSlow} onChange={(e) => update('revolvingSlow', e.target.checked)} />} label="Slow Feature" />
              <FormControlLabel control={<Checkbox size="small" checked={!!element.revolvingWide} onChange={(e) => update('revolvingWide', e.target.checked)} />} label="Wide Compartment" />
            </>
          )}
        </Box>
      )}

      {/* Fan-specific */}
      {isFan && (
        <TextField label="Fan Type" size="small" select fullWidth value={element.fanType || 'ceiling'} onChange={(e) => update('fanType', e.target.value)} sx={{ mb: 1 }}>
          <MenuItem value="ceiling">Ceiling</MenuItem>
          <MenuItem value="standing">Standing</MenuItem>
          <MenuItem value="wall">Wall-Mounted</MenuItem>
        </TextField>
      )}

      {/* Restroom-specific */}
      {isRestroom && (
        <>
          <FormControlLabel control={<Checkbox size="small" checked={!!element.accessible} onChange={(e) => update('accessible', e.target.checked)} />} label="Accessible" />
          <TextField label="Bathroom Size" size="small" select fullWidth value={element.bathroomSize || 'standard'} onChange={(e) => update('bathroomSize', e.target.value)} sx={{ mb: 1 }}>
            <MenuItem value="small">Small</MenuItem>
            <MenuItem value="standard">Standard</MenuItem>
            <MenuItem value="large">Large</MenuItem>
          </TextField>
          <TextField label="Ownership" size="small" select fullWidth value={element.ownership || 'private'} onChange={(e) => update('ownership', e.target.value)} sx={{ mb: 1 }}>
            <MenuItem value="private">Private (Venue Only)</MenuItem>
            <MenuItem value="shared">Shared (Multi-Venue)</MenuItem>
            <MenuItem value="public">Public</MenuItem>
          </TextField>
        </>
      )}

      {/* Disabled parking-specific */}
      {isParking && (
        <>
          <TextField label="Spot Count" size="small" type="number" fullWidth value={element.spotCount || 1} onChange={(e) => update('spotCount', Number(e.target.value))} sx={{ mb: 1 }} />
          <TextField label="Distance from Entry" size="small" fullWidth value={element.distanceFromEntry || ''} onChange={(e) => update('distanceFromEntry', e.target.value)} sx={{ mb: 1 }} placeholder="e.g. 10m" />
          <TextField label="Parking Ownership" size="small" select fullWidth value={element.parkingOwnership || 'venue'} onChange={(e) => update('parkingOwnership', e.target.value)} sx={{ mb: 1 }}>
            <MenuItem value="venue">Venue-Owned</MenuItem>
            <MenuItem value="shared">Shared/Public</MenuItem>
            <MenuItem value="street">Street</MenuItem>
          </TextField>
        </>
      )}

      {/* Zone-specific */}
      {isZone && (
        <Typography variant="caption" color="text.secondary">
          Resize this zone to cover the area on your floor plan.
        </Typography>
      )}
    </Box>
  );
};

export default PropertyPanel;
